import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, DatabaseBackup, Download, Loader2, ShieldCheck, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { fmtDate } from "@/lib/format";
import { PageHeader, btnGhost, btnPrimary, inputCls } from "@/components/app/common";

const LABEL = { impostazioni: "Dati azienda", users: "Utenti", marche: "Marche", categorie: "Categorie e tipologie", fornitori: "Fornitori", prodotti: "Prodotti", fatture: "Fatture", acquisti: "Acquisti (storico prezzi)", mappature: "Abbinamenti", movimenti: "Movimenti", ricorrenti: "Spese ricorrenti", vendite: "Vendite mensili", uscite: "Uscite merce", rettifiche: "Rettifiche", budget: "Budget", spese_brand: "Spese brand", cambi: "Cambi valuta", files: "File (metadati)", meta: "Impostazioni interne", ai_insights: "Analisi AI", conversazioni: "Conversazioni AI", messaggi: "Messaggi AI" };
const dt = (s) => (s ? `${fmtDate(s.slice(0, 10))} ${s.slice(11, 16)}` : "—");

function Scarica() {
  const [ai, setAi] = useState(true);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await downloadFile("/backup/scarica", { ai }, "cosmetica-control-backup.zip"); toast.success("Backup scaricato"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <section className="card-soft p-6 space-y-4" data-testid="backup-scarica-card">
      <h2 className="text-lg font-medium flex items-center gap-2"><Download className="h-4 w-4 text-[#8C6D62]" />Scarica backup completo</h2>
      <p className="text-sm text-[#5E4A42]">Un unico file .zip con tutti i dati: marche, categorie, fornitori, prodotti, fatture e allegati PDF, acquisti, abbinamenti, movimenti, vendite, uscite, rettifiche, budget, spese brand, dati azienda e utenti (con le loro password).</p>
      <label className="flex items-center justify-between rounded-xl border border-[#EAE3DC] px-4 py-3 text-sm">
        <span>Includi le conversazioni del Consulente AI</span>
        <Switch checked={ai} onCheckedChange={setAi} data-testid="backup-ai-switch" />
      </label>
      <Button className={btnPrimary} onClick={go} disabled={busy} data-testid="backup-scarica-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1" />Scarica backup</>}</Button>
      <p className="text-xs text-[#756B64]">Conservalo in un posto sicuro: contiene dati riservati.</p>
    </section>
  );
}

function Anteprima({ prev }) {
  const m = prev.manifest;
  return (
    <div className="rounded-xl bg-[#FAF7F5] p-4 text-sm space-y-3" data-testid="ripristino-anteprima">
      <div className="grid sm:grid-cols-3 gap-2 text-xs">
        <div><span className="text-[#756B64]">Creato il</span><div className="font-medium" data-testid="anteprima-data">{dt(m.creato_il)}</div></div>
        <div><span className="text-[#756B64]">Provenienza</span><div className="font-medium break-all" data-testid="anteprima-fonte">{m.fonte || "—"}</div></div>
        <div><span className="text-[#756B64]">Amministratori</span><div className="font-medium break-all" data-testid="anteprima-admin">{prev.amministratori.join(", ")}</div></div>
      </div>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-[#756B64]"><tr><th className="text-left py-1">Sezione</th><th className="text-right">Nel backup</th><th className="text-right">Ora qui (verrà sostituito)</th></tr></thead>
        <tbody className="divide-y divide-[#EAE3DC]">
          {Object.entries(prev.conteggi_backup).map(([k, v]) => (
            <tr key={k} data-testid={`anteprima-row-${k}`}><td className="py-1">{LABEL[k] || k}</td><td className="text-right tabular font-medium">{v}</td><td className="text-right tabular text-[#756B64]">{prev.conteggi_attuali[k]}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="text-xs text-[#756B64]">File allegati inclusi: <b>{prev.file_inclusi}</b>{m.file_non_scaricati?.length ? ` · non inclusi: ${m.file_non_scaricati.length}` : ""}</div>
    </div>
  );
}

function Report({ res }) {
  const v = res.verifica;
  const anomalie = Object.entries(v.anomalie).filter(([, n]) => n);
  return (
    <div className="rounded-xl border border-[#8AA090]/40 bg-[#8AA090]/10 p-4 text-sm space-y-2" data-testid="ripristino-report">
      <div className="font-medium flex items-center gap-2 text-[#4E6656]"><CheckCircle2 className="h-4 w-4" />Ripristino completato</div>
      <ul className="text-xs space-y-1">
        <li>Prodotti: <b data-testid="report-prodotti">{v.prodotti}</b> · giacenza totale <b data-testid="report-giacenza">{v.giacenza_totale}</b> pz · valore magazzino <b>{v.valore_magazzino} €</b></li>
        <li>Fatture: <b>{res.conteggi.fatture}</b> · acquisti: <b>{res.conteggi.acquisti}</b> · movimenti: <b>{res.conteggi.movimenti}</b> · file ripristinati: <b>{res.file_ripristinati}</b></li>
        <li>Costi medi ricalcolati diversi dal backup: <b data-testid="report-costi">{v.costi_medi_ricalcolati}</b></li>
        <li data-testid="report-anomalie">{anomalie.length ? <span className="text-[#B4553D]">Da controllare: {anomalie.map(([k, n]) => `${k.replaceAll("_", " ")} (${n})`).join(", ")}</span> : "Nessuna incoerenza trovata"}</li>
        {v.prodotti_giacenza_negativa.length > 0 && <li className="text-[#B4553D]">Giacenza negativa: {v.prodotti_giacenza_negativa.join(", ")}</li>}
      </ul>
      <div className="text-xs text-[#5E4A42]">Tra pochi secondi verrai disconnesso: accedi con le credenziali presenti nel backup.</div>
    </div>
  );
}

function Ripristina({ onDone }) {
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [testo, setTesto] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const ref = useRef();
  const scegli = async (f) => {
    setFile(f); setPrev(null); setRes(null); setTesto("");
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { setPrev((await api.post("/backup/anteprima", fd)).data); } catch (e) { toast.error(errMsg(e)); setFile(null); } finally { setBusy(false); }
  };
  const go = async () => {
    const fd = new FormData(); fd.append("file", file); fd.append("conferma", testo);
    setBusy(true);
    try {
      const { data } = await api.post("/backup/ripristina", fd, { timeout: 600000 });
      setRes(data); onDone();
      toast.success("Dati ripristinati");
      setTimeout(() => { localStorage.removeItem(TOKEN_KEY); window.location.href = "/login"; }, 12000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card-soft p-6 space-y-4" data-testid="backup-ripristina-card">
      <h2 className="text-lg font-medium flex items-center gap-2"><Upload className="h-4 w-4 text-[#8C6D62]" />Ripristina da backup</h2>
      <div className="flex gap-2 text-xs text-[#8A6A33] bg-[#CBA368]/15 rounded-xl px-3 py-2"><AlertTriangle className="h-4 w-4 shrink-0" />Sostituisce TUTTI i dati di questa installazione (utenti compresi) con quelli del backup. Prima viene salvata automaticamente una copia di sicurezza dei dati attuali.</div>
      <input ref={ref} type="file" accept=".zip,application/zip" className="hidden" data-testid="ripristino-file-input" onChange={(e) => scegli(e.target.files?.[0] || null)} />
      <Button variant="outline" className={btnGhost} onClick={() => ref.current?.click()} disabled={busy} data-testid="ripristino-scegli-button">{busy && !prev ? <Loader2 className="h-4 w-4 animate-spin" /> : <><DatabaseBackup className="h-4 w-4 mr-1" />{file ? file.name : "Scegli il file di backup (.zip)"}</>}</Button>
      {prev && !res && (
        <>
          <Anteprima prev={prev} />
          <label className="block text-sm">Per confermare scrivi <b>RIPRISTINA</b>
            <Input data-testid="ripristino-conferma-input" value={testo} onChange={(e) => setTesto(e.target.value)} className={`${inputCls} mt-1`} placeholder="RIPRISTINA" />
          </label>
          <Button className="rounded-full bg-[#B4553D] hover:bg-[#9A4532] text-white" onClick={go} disabled={busy || testo.trim() !== "RIPRISTINA"} data-testid="ripristino-conferma-button">{busy ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Ripristino in corso…</> : "Sostituisci tutti i dati con il backup"}</Button>
        </>
      )}
      {res && <Report res={res} />}
    </section>
  );
}

function CopieSicurezza({ items }) {
  if (!items.length) return null;
  const get = (b) => downloadFile(`/backup/sicurezza/${b.id}`, {}, "copia-sicurezza.zip").catch((e) => toast.error(e.message));
  return (
    <section className="card-soft p-6 space-y-3 lg:col-span-2" data-testid="backup-sicurezza-card">
      <h2 className="text-lg font-medium flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#8C6D62]" />Copie di sicurezza automatiche</h2>
      <p className="text-xs text-[#756B64]">Salvate prima di ogni ripristino: contengono i dati com'erano prima. Si possono ripristinare con «Ripristina da backup».</p>
      <ul className="divide-y divide-[#EAE3DC] text-sm">
        {items.map((b, i) => (
          <li key={b.id} className="flex items-center justify-between py-2" data-testid={`sicurezza-row-${i}`}>
            <span>{dt(b.created_at)} · {b.created_by} · {b.conteggi?.prodotti ?? 0} prodotti, {b.conteggi?.fatture ?? 0} fatture</span>
            <Button variant="outline" size="sm" className={btnGhost} onClick={() => get(b)} data-testid={`sicurezza-scarica-${i}`}><Download className="h-3.5 w-3.5 mr-1" />Scarica</Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function Backup() {
  const [sic, setSic] = useState([]);
  const load = useCallback(() => api.get("/backup/sicurezza").then((r) => setSic(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="backup-page">
      <PageHeader overline="Impostazioni" title="Backup e ripristino" subtitle="Scarica tutti i dati in un file, oppure trasferiscili da un'altra installazione (es. dall'anteprima al tuo dominio)." />
      <div className="grid lg:grid-cols-2 gap-5 max-w-5xl">
        <Scarica />
        <Ripristina onDone={load} />
        <CopieSicurezza items={sic} />
      </div>
    </div>
  );
}
